import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Empty, ErrorMsg, Loading, Modal, PageHead, Tabs, Time } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import type { Asset, ContentStatus, ItemRow, Project, TemplateRow, UsageStatus } from '../../lib/contentTypes';
import { BrandPanel } from './Brand';
import { PublicationsTab } from './Publications';
import { AssetCard, ContentStatusBadge, FORMAT_LABEL, STATUS_LABEL, USAGE_LABEL, uploadAssets, useContentMeta } from './common';

type Tab = 'items' | 'projects' | 'publications' | 'assets' | 'brand';

export function ContentHome() {
  const [sp, setSp] = useSearchParams();
  const tab = (sp.get('tab') as Tab) || 'items';
  const [newItem, setNewItem] = useState(false);
  const [newProject, setNewProject] = useState(false);
  return (
    <div className="page">
      <PageHead title="Content Studio" meta="Still images, posters and carousels for Mirage's own marketing">
        <Link to="/content/prepare" className="btn">
          Prepare images
        </Link>
        <Link to="/content/templates" className="btn">
          Templates
        </Link>
        <button type="button" className="btn" onClick={() => setNewProject(true)}>
          New project
        </button>
        <button type="button" className="btn primary" onClick={() => setNewItem(true)}>
          New content item
        </button>
      </PageHead>
      <p className="muted small cs-honesty">
        Exporting produces files; it does not publish anything. Templates fill text only from a project's public fields. Text suggestions come from the local model through an agent and are proposals you edit; the Publications tab keeps each publication's folder, files and texts per account. Video editing stays in Mirage Video.
      </p>
      <Tabs<Tab>
        tabs={[
          { id: 'items', label: 'Items' },
          { id: 'projects', label: 'Projects' },
          { id: 'publications', label: 'Publications' },
          { id: 'assets', label: 'Assets' },
          { id: 'brand', label: 'Brand & templates' },
        ]}
        value={tab}
        onChange={(t) => setSp(t === 'items' ? {} : { tab: t })}
      />
      {tab === 'items' && <ItemsTab />}
      {tab === 'projects' && <ProjectsTab onNew={() => setNewProject(true)} />}
      {tab === 'publications' && <PublicationsTab />}
      {tab === 'assets' && <AssetsTab />}
      {tab === 'brand' && <BrandPanel />}
      {newItem && <NewItemDialog onClose={() => setNewItem(false)} />}
      {newProject && <NewProjectDialog onClose={() => setNewProject(false)} />}
    </div>
  );
}

function ItemsTab() {
  const [status, setStatus] = useState<string>('');
  const [data, setData] = useState<{ items: ItemRow[]; counts: Record<string, number> } | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api<{ items: ItemRow[]; counts: Record<string, number> }>(`/api/content-items${status ? `?status=${status}` : ''}`)
      .then(setData)
      .catch((e) => setError(errorText(e)));
  }, [status]);
  if (error) return <ErrorMsg>{error}</ErrorMsg>;
  if (!data) return <Loading what="Loading content" />;
  return (
    <section className="section">
      <div className="row gap wrap center cs-filter">
        <label className="field cs-inline">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
            <option value="">all</option>
            {(Object.keys(STATUS_LABEL) as ContentStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]} ({data.counts[s] ?? 0})
              </option>
            ))}
          </select>
        </label>
      </div>
      {data.items.length === 0 ? (
        <Empty>No content items{status ? ` with status ${STATUS_LABEL[status as ContentStatus]}` : ''} yet. Create one from a template with “New content item”.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Project</th>
                <th>Format</th>
                <th>Status</th>
                <th>Revision</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.id}>
                  <td>
                    <Link to={`/content/items/${i.id}`}>{i.title}</Link>
                  </td>
                  <td>{i.project_title ?? <span className="muted">—</span>}</td>
                  <td className="small">
                    {FORMAT_LABEL[i.format]} <span className="muted num">{i.width}×{i.height}</span>
                  </td>
                  <td>
                    <ContentStatusBadge status={i.status} />
                  </td>
                  <td className="num">{i.revision_number ?? '—'}</td>
                  <td>
                    <Time iso={i.updated_at} relative />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ProjectsTab({ onNew }: { onNew: () => void }) {
  const [items, setItems] = useState<Project[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api<{ items: Project[] }>('/api/content/projects')
      .then((d) => setItems(d.items))
      .catch((e) => setError(errorText(e)));
  }, []);
  if (error) return <ErrorMsg>{error}</ErrorMsg>;
  if (!items) return <Loading what="Loading projects" />;
  if (!items.length)
    return (
      <Empty>
        No projects yet. A project holds the approved public facts and images of one Mirage job.{' '}
        <button type="button" className="link" onClick={onNew}>
          Create the first project
        </button>
      </Empty>
    );
  return (
    <ul className="plain cs-projects">
      {items.map((p) => (
        <li key={p.id}>
          <Link to={`/content/projects/${p.id}`} className="cs-project-title">
            {p.title}
          </Link>
          <span className="muted small">
            {p.location ?? 'location not set'} · {p.asset_count} image{p.asset_count === 1 ? '' : 's'} · {p.item_count} item{p.item_count === 1 ? '' : 's'} · publication{' '}
            {p.publication_permission.replace('_', ' ')}
          </span>
        </li>
      ))}
    </ul>
  );
}

function AssetsTab() {
  const [items, setItems] = useState<Asset[] | null>(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(() => {
    api<{ items: Asset[] }>('/api/content/assets')
      .then((d) => setItems(d.items))
      .catch((e) => setError(errorText(e)));
  }, []);
  useEffect(load, [load]);
  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    const { done, errors } = await uploadAssets(Array.from(files), { onProgress: setMsg });
    setMsg(`${done.length} uploaded${errors.length ? `; refused: ${errors.join('; ')}` : ''}`);
    load();
  }
  return (
    <section className="section">
      <div className="row gap wrap center">
        <label className="btn file-btn">
          Upload images
          <input type="file" accept="image/jpeg,image/png" multiple onChange={(e) => void onFiles(e.target.files)} aria-label="Upload images" />
        </label>
        <span className="muted small">JPEG or PNG. Originals are stored unchanged; previews are derived copies.</span>
      </div>
      {msg && <p className="msg info">{msg}</p>}
      <ErrorMsg>{error}</ErrorMsg>
      {!items ? (
        <Loading what="Loading assets" />
      ) : items.length === 0 ? (
        <Empty>No images yet.</Empty>
      ) : (
        <div className="cs-assets">
          {items.map((a) => (
            <AssetCard key={a.id} asset={a}>
              <UsageSelect asset={a} onChange={load} />
              <Link className="small" to={`/content/prepare?asset=${a.id}`}>
                Reframe…
              </Link>
            </AssetCard>
          ))}
        </div>
      )}
    </section>
  );
}

export function UsageSelect({ asset, onChange }: { asset: Asset; onChange: () => void }) {
  const [error, setError] = useState('');
  return (
    <>
      <select
        aria-label={`Usage of ${asset.filename}`}
        value={asset.usage_status}
        onChange={(e) =>
          api(`/api/content/assets/${asset.id}`, { method: 'PATCH', body: { usage_status: e.target.value as UsageStatus } })
            .then(onChange)
            .catch((err) => setError(errorText(err)))
        }
      >
        {(Object.keys(USAGE_LABEL) as UsageStatus[]).map((u) => (
          <option key={u} value={u}>
            {USAGE_LABEL[u]}
          </option>
        ))}
      </select>
      {error && <span className="err-text small">{error}</span>}
    </>
  );
}

function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const p = await api<Project>('/api/content/projects', { method: 'POST', body: { title, location: location || null } });
      navigate(`/content/projects/${p.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <Modal
      title="New project"
      onClose={onClose}
      footer={
        <button type="submit" form="new-project" className="btn primary" disabled={!title.trim()}>
          Create project
        </button>
      }
    >
      <form id="new-project" className="stack" onSubmit={submit} aria-label="New project">
        <label className="field">
          <span>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </label>
        <label className="field">
          <span>Location (public)</span>
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, country" />
        </label>
        <p className="muted small">The rest (public description, client, permissions, embargo) is on the project page.</p>
        <ErrorMsg>{error}</ErrorMsg>
      </form>
    </Modal>
  );
}

export function NewItemDialog({ onClose, projectId }: { onClose: () => void; projectId?: string }) {
  const navigate = useNavigate();
  const meta = useContentMeta();
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [f, setF] = useState({ title: '', project_id: projectId ?? '', template_id: '', preset: 'portrait', width: '1080', height: '1350' });
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<{ items: TemplateRow[] }>('/api/content/templates').then((d) => {
      // only Ready templates (and the built-in ones) are offered: a Draft is still being made
      const ready = d.items.filter((t) => t.status !== 'draft');
      setTemplates(ready);
      setF((x) => ({ ...x, template_id: x.template_id || ready[0]?.id || '' }));
    });
    api<{ items: Project[] }>('/api/content/projects').then((d) => setProjects(d.items));
  }, []);
  useEffect(() => {
    api<{ items: Asset[] }>(`/api/content/assets${f.project_id ? `?project_id=${f.project_id}` : ''}`).then((d) => setAssets(d.items.filter((a) => a.kind === 'image')));
  }, [f.project_id]);
  const tpl = templates.find((t) => t.id === f.template_id);
  const slots = tpl ? new Set(tpl.layout.artboards.flatMap((a) => a.layers.map((l) => (l as { slot?: number }).slot).filter((s) => s != null))).size : 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const body: Record<string, unknown> = { title: f.title, template_id: f.template_id, preset: f.preset, asset_ids: picked, project_id: f.project_id || null };
      if (f.preset === 'custom') Object.assign(body, { width: Number(f.width), height: Number(f.height) });
      const item = await api<ItemRow>('/api/content-items', { method: 'POST', body });
      navigate(`/content/items/${item.id}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal
      title="New content item"
      onClose={onClose}
      wide
      footer={
        <button type="submit" form="new-item" className="btn primary" disabled={busy || !f.title.trim() || !f.template_id}>
          Create and open the editor
        </button>
      }
    >
      <form id="new-item" className="form-grid" onSubmit={submit} aria-label="New content item">
        <label className="field">
          <span>Title</span>
          <input value={f.title} onChange={set('title')} required autoFocus />
        </label>
        <label className="field">
          <span>Project</span>
          <select value={f.project_id} onChange={set('project_id')}>
            <option value="">no project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="field span2">
          <legend>Template</legend>
          <div className="cs-templates">
            {templates.map((t) => (
              <label key={t.id} className={`cs-template${f.template_id === t.id ? ' selected' : ''}`}>
                <input type="radio" name="template" value={t.id} checked={f.template_id === t.id} onChange={set('template_id')} />
                <strong>{t.name}</strong>
                <span className="muted small">
                  {FORMAT_LABEL[t.format]} · {t.layout.artboards.length} artboard{t.layout.artboards.length === 1 ? '' : 's'}
                  {t.is_builtin ? '' : ' · yours'}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="field">
          <span>Size</span>
          <select value={f.preset} onChange={set('preset')} aria-label="Size preset">
            {meta?.presets.map((p) => (
              <option key={p.key} value={p.key}>
                {p.key} {p.width}×{p.height}
              </option>
            ))}
            <option value="custom">custom</option>
          </select>
        </label>
        {f.preset === 'custom' ? (
          <div className="row gap">
            <label className="field">
              <span>Width px</span>
              <input type="number" min={200} max={8000} value={f.width} onChange={set('width')} />
            </label>
            <label className="field">
              <span>Height px</span>
              <input type="number" min={200} max={8000} value={f.height} onChange={set('height')} />
            </label>
          </div>
        ) : (
          <p className="muted small">Presets are editable starting points, not platform rules.</p>
        )}
        <fieldset className="field span2">
          <legend>
            Images, in order ({picked.length} chosen{slots ? `; this template has ${slots} image slot${slots === 1 ? '' : 's'}` : ''})
          </legend>
          {assets.length === 0 ? (
            <p className="muted small">No images{f.project_id ? ' in this project' : ''} yet. Upload them on the project page or the Assets tab; you can also add images in the editor.</p>
          ) : (
            <div className="cs-assets small-grid">
              {assets.map((a) => (
                <AssetCard
                  key={a.id}
                  asset={a}
                  selected={picked.includes(a.id) ? picked.indexOf(a.id) : null}
                  onSelect={() => setPicked((p) => (p.includes(a.id) ? p.filter((x) => x !== a.id) : [...p, a.id]))}
                />
              ))}
            </div>
          )}
        </fieldset>
        <div className="span2">
          <ErrorMsg>{error}</ErrorMsg>
        </div>
      </form>
    </Modal>
  );
}
